import BoPagination from "components/bo/Pagination/BoPagination";
import Button from "core/buttons/button/Button";
import { ACTIONS, ENTITIES, VAR_TYPES } from "constants/constants";
import { FormattedMessage } from "react-intl";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { TableManager } from "components/bo/admin-table/TableManager";
import {
  navigateView,
  navigateEdit,
  navigateToParent,
  handleUpload,
} from "utils/boHelper";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useEffect, useState } from "react";
import { useExchangeRateContext } from "api/settings/exchange-rate/ExchangeRateProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "api/user/UserProvider";
import { getFormattedDate } from "utils/functions";

export default function AdminExchangeRate({ action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [inputs, setInputs] = useState([]);
  const [uploadFileInputs, setUploadFileInputs] = useState([]);
  const defaultTotalItems = 0;
  const defaultPage = 1;
  const defaultItemsPerPage = 25;
  const {
    exchangeRateByIdGetter,
    exchangeRateCreate,
    exchangeRateDeleteById,
    exchangeRateList,
    exchangeRateListGetter,
    exchangeRatePerId,
    exchangeRateUpdateById,
    uploadExchangeRate,
  } = useExchangeRateContext();
  const { total_items, items_per_page, page } = exchangeRateList;
  const { countryList, countryListGetter } = useCountryContext();
  const { currencyList, currencyListGetter } = useCurrencyContext();
  const { userScope, lang } = useUserContext();

  useEffect(() => {
    if (currencyList.length > 0 && countryList.length > 0) {
      const choices_currency = currencyList.map((e) => ({
        name: e.currency_name,
        value: e.id,
      }));

      const choices_country = countryList.map((e) => ({
        name: e.name,
        value: e.id,
      }));

      const countryInput = {
        col: "country_id",
        mandatory: true,
        isMissingValue: null,
        editable: true,
        type: VAR_TYPES.DROPDOWN,
        choices: choices_country,
      };

      setInputs([
        {
          col: "base_currency_id",
          mandatory: true,
          isMissingValue: null,
          editable: true,
          type: VAR_TYPES.DROPDOWN,
          choices: choices_currency,
        },
        {
          col: "objective_currency_id",
          mandatory: true,
          isMissingValue: null,
          editable: true,
          type: VAR_TYPES.DROPDOWN,
          choices: choices_currency,
        },
        {
          col: "convertion_rate",
          mandatory: true,
          isMissingValue: null,
          editable: true,
          type: VAR_TYPES.NUMBER,
        },
        countryInput,
        {
          col: "import_date",
          mandatory: true,
          isMissingValue: null,
          editable: true,
          type: VAR_TYPES.DATE,
        },
      ]);
      setUploadFileInputs([
        countryInput,
        {
          col: "file",
          mandatory: true,
          isMissingValue: null,
          type: VAR_TYPES.FILE,
          fileType: "application/csv",
          label: (
            <FormattedMessage
              id={"upload_exchange_rate_file_format_label"}
              defaultMessage={
                "Format du fichier: Origin Currency, Change Currency, Change Value, Import Date (2018-12-24)"
              }
            />
          ),
        },
      ]);
    }
  }, [currencyList, countryList]);

  const [deleteData, setDeleteData] = useState(null);

  useEffect(() => {
    exchangeRateListGetter();
    countryListGetter();
    currencyListGetter();
  }, [exchangeRateListGetter, countryListGetter, currencyListGetter]);

  useEffect(() => {
    if (id) {
      exchangeRateByIdGetter(id);
    }
  }, [id, exchangeRateByIdGetter]);

  const handleDelete = async (exchangeRateId) =>
    setDeleteData(
      exchangeRateList?.content?.find((e) => e.id === exchangeRateId)
    );

  const validateDelete = async (exchangeRateId) => {
    try {
      await exchangeRateDeleteById(exchangeRateId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreate = (payload) => {
    try {
      exchangeRateCreate(payload);
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (payload) => {
    try {
      exchangeRateUpdateById(payload.id, payload);
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  const handleChangePage = (e) => exchangeRateListGetter(e, items_per_page);

  const formatList = (list) =>
    list.map((elm) => ({
      ...elm,
      base_currency_id: elm.base_currency_id
        ? currencyList.find((e) => e.id === elm.base_currency_id)?.currency_name
        : "",
      objective_currency_id: elm.objective_currency_id
        ? currencyList.find((e) => e.id === elm.objective_currency_id)
            ?.currency_name
        : "",
      country_id: elm.country_id
        ? countryList.find((e) => e.id === elm.country_id)?.name
        : "",
      import_date: getFormattedDate(elm?.import_date, lang),
    }));

  const formatExchangeRate = (exchangeRate) => ({
    ...exchangeRate,
    base_currency_id:
      currencyList.find((e) => e.id === exchangeRate?.base_currency_id)
        ?.currency_name || "N/A",
    objective_currency_id:
      currencyList.find((e) => e.id === exchangeRate?.objective_currency_id)
        ?.currency_name || "N/A",
    country_id:
      countryList.find((e) => e.id === exchangeRate?.country_id)?.name || "N/A",
  });

  return (
    <div>
      <div>
        {action === ACTIONS.LIST && (
          <div className="flex">
            <Button
              className="btn-hover-color"
              size="large"
              color="blue"
              onClick={() => navigateView("upload", navigate, location)}
              margin="0 12px"
            >
              <FormattedMessage
                id={"admin_upload_exchange_rate"}
                defaultMessage={"Télécharger taux de change"}
              />
            </Button>
            <Button
              className="btn-hover-color"
              size="large"
              color="blue"
              onClick={() => navigateView("new", navigate, location)}
              margin="0 12px"
            >
              <FormattedMessage
                id={"admin_create_new"}
                defaultMessage={"Nouveau"}
              />
            </Button>
          </div>
        )}
        <br />
        <TableManager
          action={action}
          entity={ENTITIES.EXCHANGE_RATE}
          entityPerId={exchangeRatePerId}
          formattedEntity={formatExchangeRate(exchangeRatePerId)}
          list={formatList(
            exchangeRateList?.content?.length > 0
              ? exchangeRateList.content
              : []
          )}
          inputs={inputs}
          uploadFileInputs={uploadFileInputs}
          navigateEdit={(propsId) => navigateEdit(propsId, navigate, location)}
          navigateView={(propsId) => navigateView(propsId, navigate, location)}
          handleCreate={handleCreate}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          handleUpload={handleUpload}
          uploadExchangeRate={uploadExchangeRate}
        />
        {location.pathname === `/${userScope}/exchange-rate` && (
          <div className="center-pagination">
            <BoPagination
              total={total_items || defaultTotalItems}
              limit={items_per_page || defaultItemsPerPage}
              page={page || defaultPage}
              onChange={(e) => handleChangePage(e)}
            />
          </div>
        )}
        <br />
        <div id="modalDelete">
          <HandleModaleBO
            setDeleteData={setDeleteData}
            validateDelete={validateDelete}
            deleteData={deleteData}
            entity={ENTITIES.EXCHANGE_RATE}
          />
        </div>
      </div>
    </div>
  );
}
